/**
 * Everything the kitchen shows about time, derived rather than drawn.
 *
 * The canvas code below this is a handful of `fillRect` calls; the decisions —
 * how far along the cook is, how loudly the pan should be complaining, what
 * colour a bar is — live here as pure functions of a station and a tick. That
 * split is what makes the visual half of the renderer testable without a
 * browser, and it is where the escalating burn cue actually lives.
 *
 * Nothing here reads `timings.json`. The pan carries its own phase stamps and
 * the board and sink carry their own targets, so a stored log from before a
 * retuning still renders its own bars correctly.
 */

import type { CookState, IngredientId, Station, StationType } from '@overcooked/shared'
import { containerOnStation, heldProgressFraction, phaseFraction } from '@overcooked/shared'
import { COLOURS, mixHex } from './theme'

/**
 * How loudly a bar is drawn.
 *
 * `normal` is the cook bar, which the player watches from across the kitchen
 * while doing something else. `hold` is the bar under a key they are holding
 * *right now* — chop, wash, extinguish — and it is drawn thicker, because the
 * complaint the whole of phase 5 exists to answer is "I held the key and
 * nothing happened". A hairline that grows over two and a half seconds is not
 * an answer; a bar you cannot miss is.
 */
type BarWeight = 'normal' | 'hold'

export interface Bar {
  /** 0..1 along the bar. */
  fraction: number
  /** Fill colour, already resolved — the caller does no lerping. */
  colour: string
  /** 0..1 pulse depth. Zero for a calm bar; the burn cue drives this up. */
  pulse: number
  /** A short word for the state, drawn above the bar. */
  label: string
  /** Which of the two thicknesses to draw at. */
  weight: BarWeight
  /**
   * Hatch the fill with diagonal stripes. Set on a paused hold, so "nobody is
   * on this" differs from a live bar by pattern as well as colour — seat 2's
   * blue and the old paused ice were close enough to read as the same bar.
   */
  striped?: boolean
}

interface PanDisplay {
  /** Which vessel this is, named by the content rather than by the renderer. */
  kind: string
  state: CookState
  contents: IngredientId[]
  /** `null` for an empty pan, which shows nothing at all. */
  bar: Bar | null
  /**
   * True once the food is cooked and the burn clock is running. The renderer
   * uses it to ring the whole tile, so the warning is visible from across the
   * kitchen rather than only if you are looking at the bar.
   */
  warning: boolean
}

// One palette for the whole game (see theme.ts) — the amber a pan turns as it
// approaches burning is the same amber a ticket turns as it approaches expiry,
// deliberately, so "running out of time" looks like one thing.
const COOKING_COLOUR = COLOURS.mint
const DONE_COLOUR = COLOURS.brass
const BURNING_COLOUR = COLOURS.blood
const BURNT_COLOUR = '#332621'
const FIRE_COLOUR = COLOURS.ember
// Neutral grey, not a hue: no seat colour can be mistaken for it, and the
// stripes `drawBar` adds carry the meaning for anyone who cannot tell colours
// apart at all.
export const PAUSED_COLOUR = COLOURS.steelLight

/** Blend two `#rrggbb` colours. Re-exported from the theme so a bar ramp and a
 * ticket fuse cannot drift apart. */
export const mix = mixHex

/**
 * The pan, as the player needs to understand it.
 *
 * Two phases, two meanings, one bar. While cooking it fills green toward a
 * marked `done`. Once done, the same bar refills — and this is the escalating
 * cue: it ramps amber to red and pulses harder the
 * closer the food gets to being lost. A single cue, not the four-stage ladder
 * the original doc wanted, because the point is "act now", not "act in stage 3".
 */
export function panDisplay(station: Station, atTick: number): PanDisplay | null {
  const pan = containerOnStation(station)
  if (!pan) return null

  if (pan.cookState === 'empty') {
    return { kind: pan.kind, state: 'empty', contents: [], bar: null, warning: false }
  }

  const fraction = phaseFraction(pan.phaseStartedAtTick, pan.phaseEndsAtTick, atTick) ?? 0

  if (pan.cookState === 'burnt') {
    // Time constraint #4: burnt is not the end any more. While the third clock
    // runs, the bar keeps filling — in the colour of the fire it is counting
    // down to — so "this is about to become a real problem" is visible.
    return {
      kind: pan.kind,
      state: 'burnt',
      contents: [...pan.contents],
      bar:
        pan.phaseEndsAtTick === null
          ? { fraction: 1, colour: BURNT_COLOUR, pulse: 0, label: 'Burnt', weight: 'normal' }
          : {
              fraction,
              colour: mix(BURNT_COLOUR, FIRE_COLOUR, fraction),
              pulse: 1,
              label: 'Burnt! Bin it',
              weight: 'normal',
            },
      warning: pan.phaseEndsAtTick !== null,
    }
  }

  if (pan.cookState === 'cooking') {
    return {
      kind: pan.kind,
      state: 'cooking',
      contents: [...pan.contents],
      bar: { fraction, colour: COOKING_COLOUR, pulse: 0, label: 'Cooking', weight: 'normal' },
      warning: false,
    }
  }

  // `done`: the food is ready and the burn clock is running.
  return {
    kind: pan.kind,
    state: 'done',
    contents: [...pan.contents],
    bar: {
      fraction,
      colour: mix(DONE_COLOUR, BURNING_COLOUR, fraction),
      // Calm at first, frantic at the end. Squared so the last third of the
      // window is unmistakably different from the first.
      pulse: fraction * fraction,
      label: fraction > 0.6 ? 'Burning!' : 'Ready',
      weight: 'normal',
    },
    warning: true,
  }
}

/**
 * The chop bar on the board and the wash bar at the sink.
 *
 * `holderColour` is the seat colour of whoever has the key down, and it is the
 * one piece of attribution on a bar. Held progress is station-scoped by design
 * — anybody can pick up anybody's chop — which left both chefs looking at
 * identical bars and unable to answer "is my partner on that, or is nobody".
 * Tinting the fill answers it at a glance without adding any state: `heldBy` is
 * already folded, and the seat it belongs to is already resolved at render time
 * for the extinguisher's spray angle.
 *
 * The colour is passed in rather than looked up because a seat lives in
 * `state.players` and this module deliberately knows only about one station.
 * A frozen bar has no holder: it turns neutral grey and striped, which is what
 * makes "somebody walked away from this" visible — and, unlike the ice blue it
 * used to be, never looks like seat 2 still holding the key.
 */
export function holdDisplay(station: Station, atTick: number, holderColour: string | null = null): Bar | null {
  const fraction = heldProgressFraction(station, atTick)
  if (fraction === null) return null
  const resumed = station.resumedAtTick !== null
  return {
    fraction,
    colour: resumed && holderColour ? holderColour : PAUSED_COLOUR,
    pulse: 0,
    // A frozen bar reads as abandoned work rather than as a stalled game.
    label: resumed ? holdVerb(station) : 'Paused',
    weight: 'hold',
    striped: !resumed,
  }
}

function holdVerb(station: Station): string {
  if (station.onFire) return 'Putting it out'
  return station.type === 'sink' ? 'Washing' : 'Chopping'
}

// ---------------------------------------------------------------------------
// The hold, made visible
//
// The engine has always been right about holds: a hold accumulates, survives
// release, and resumes rather than restarts (`packages/engine/src/fire.test.ts`
// pins all three). What was missing was any way to *see* it, which is why the
// reported bug was "spacebar does not work on the extinguisher" rather than
// "the extinguisher takes longer than I expected". Nothing below changes a
// rule; every one of these is a pure question about a station that the canvas
// then answers in paint.
// ---------------------------------------------------------------------------

/**
 * Stations whose work is done by *holding* the key rather than tapping it.
 *
 * Three of them, and until now nothing on screen said which three: board, sink,
 * and any station that has caught fire. The tile draws a "hold" glyph for
 * exactly this set, so the tap/hold split is discoverable before the player
 * commits to a press rather than after they conclude the key is broken.
 *
 * A `stove` or an `oven` is deliberately absent. Holding at a hob is a silent
 * no-op in the engine (`interact.ts:378`) and stays one — but the *edge* of
 * that same press already resolves to a pick-up, a put-down, or a refusal that
 * flashes, so the press is acknowledged. What was missing there was never
 * feedback; it was the promise of feedback, and not drawing the glyph is that
 * promise being kept.
 */
const HOLD_STATIONS: ReadonlySet<StationType> = new Set<StationType>(['board', 'sink'])

export function holdAffordance(station: Station): boolean {
  // No fixture stands on a floor or a wall, so there is nothing to badge — and
  // a burning one would be a bug elsewhere, not a hold worth advertising.
  if (station.type === 'floor' || station.type === 'wall') return false
  if (station.onFire) return true
  return HOLD_STATIONS.has(station.type)
}

/**
 * How far along putting this fire out is, 0..1.
 *
 * `0` covers both "nobody has started" and "not on fire", which is what the
 * renderer wants: the flame is drawn at full size in both cases. Note this
 * keeps counting a *paused* extinguish — progress that survived a release is
 * real progress, and a fire that sprang back to full height the moment a player
 * let go would tell exactly the lie this phase is fixing.
 */
export function extinguishProgress(station: Station, atTick: number): number {
  if (!station.onFire) return 0
  return heldProgressFraction(station, atTick) ?? 0
}

/** True while someone is holding the key at this fire *right now* — the spray. */
export function isSpraying(station: Station): boolean {
  return station.onFire && station.resumedAtTick !== null && station.heldBy !== null
}

/**
 * Where a bar sits inside a tile, and how thick it is.
 *
 * Pulled out of `stations.ts` so the one number this phase changes — the height
 * of a hold bar — is a checkable decision rather than a literal buried in a
 * `fillRect`. Everything is proportional to the tile, so the same ratios hold
 * from a 16-pixel tile on a phone to a 132-pixel one on a monitor.
 */
interface BarMetrics {
  x: number
  top: number
  width: number
  height: number
}

/** The cook bar: watched from across the kitchen, but never held. */
export const BAR_HEIGHT_RATIO = 0.16
/** The hold bar: read at a glance, while the player's finger is on the key. */
export const HOLD_BAR_HEIGHT_RATIO = 0.26
const BAR_INSET_RATIO = 0.09

export function barMetrics(
  box: { x: number; y: number; size: number },
  weight: BarWeight,
): BarMetrics {
  const { x, y, size } = box
  const ratio = weight === 'hold' ? HOLD_BAR_HEIGHT_RATIO : BAR_HEIGHT_RATIO
  // The floor rises with the ratio too: four pixels of hold bar on a tiny tile
  // is the same invisible hairline in a smaller kitchen.
  const height = Math.max(weight === 'hold' ? 6 : 4, size * ratio)
  const inset = size * BAR_INSET_RATIO
  return {
    x: x + inset,
    top: y + size - height - inset * 0.5,
    width: size - inset * 2,
    height,
  }
}

// The tick these bars are drawn against used to be guessed here, by
// `estimateTick`, from however long ago the last frame arrived. It now comes
// from `tickClock.ts`, anchored to the instant the round started — see the note
// at the top of that file for what the guess got wrong.

/** What to write in the middle of a station tile. Short, or it will not fit. */
export function itemLabel(item: Station['item']): string | null {
  if (!item) return null
  if (item.type === 'ingredient') return short(item.id)
  if (item.type === 'equipment') return item.id === 'extinguisher' ? 'extinguisher' : item.id
  if (item.type === 'container') {
    const c = item.container
    return c.contents.length === 0 ? c.kind : `${c.kind}: ${c.contents.map(short).join('+')}`
  }
  const plate = item.plate
  if (plate.contents.length === 0) return plate.clean ? 'plate' : 'dirty'
  return plate.contents.map(short).join('+')
}

/**
 * A tile is forty pixels wide, so an ingredient id has to lose its qualifier
 * before it will fit. Derived rather than tabulated, because the ingredient
 * list is data and a hand-written table would go stale the first time somebody
 * added a vegetable.
 */
export function short(id: IngredientId): string {
  const parts = id.split('_')
  const head = parts.length > 1 ? parts[0].slice(0, 1) + '.' : ''
  return (head + parts[parts.length - 1]).slice(0, 9)
}
